import React from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import cn from 'classnames'
import { Waypoint } from 'react-waypoint'

import { Container } from '@components/Container'
import { NavigationReadingPage } from '@components/NavigationReadingPage'
import { ShareButtons } from '@features/story/pages/StoryPage/components/ShareButtons'
import { Comment } from '@components/Comment'
import { BookEnum } from '@interfaces/BookEnum'
import { TiptapViewer } from '@components/TiptapViewer'
import { HorizontalStyle } from '@components/HorizontalStyle'
import { VerticalStyle } from '@components/VerticalStyle'
import { ReadingPageHeader } from '@components/ReadingPageHeader'
import { OrientationEnum } from '@interfaces/OrientationEnum'
import { DisabledCopyContent } from '@components/DisabledCopyContent'
import { GoToTopButton } from '@components/GoToTopButton'
import { ReadingPageFooter } from '@components/ReadingPageFooter'
import { ChapterPageContainer } from '@features/myWriting/pages/ChapterPage/components/ChapterPageContainer'
import { BASE_URL } from '@configs/config'
import { ThemeEnum } from '@hooks/contexts/ThemeContext/interface'
import { UserMissionEnum } from '@interfaces/UserMissionEnum'
import { FontFamilyEnum } from '@interfaces/FontFamilyEnum'
import { Loading } from '@components/Loading'
import { UserReadingToLocal } from './components/UserReadingToLocal'
import { ChapterPageProps } from './interface'
import CreateUserReading from './components/CreateUserReading'
import CaptureScreen from './components/CaptureScreen'

export function ChapterPage({
  data,
  readingPageFooterdata,
  shareUrl,
  chapterId,
  continuePageId,
  continuePercentage,
  bookId,
  isBookOnShelf,
  showData,
  isBookmarked,
  metaData,
  theme,
  fontSize,
  lineHeight,
  fontFamily,
  isLoading,
  disableWayPoint,
  handleNovelContentWayPoint,
  handlePercentageChange,
}: ChapterPageProps) {
  const { query } = useRouter()

  return (
    <div className='select-none'>
      <Head>
        <title>{`MaReads มารี้ด | ${metaData.book.title} ${metaData.chapterName} ${metaData.book.category.name} ${metaData.book.penName.firstPenName}`}</title>
        <meta
          property='og:url'
          content={`${BASE_URL}/story/${bookId}/chapter/${chapterId}`}
        />
        <meta property='og:type' content='article' />
        <meta property='og:title' content={metaData.book.title} />
        <meta
          property='og:description'
          content={`${metaData.chapterTitle} ${metaData.chapterName}`}
        />
        <meta name='twitter:card' content='summary_large_image' />
        <meta
          property='twitter:image'
          content={
            metaData.book.twitterShareImageUrl || metaData.book.coverImgPath
          }
        />
        <meta
          property='og:image'
          content={
            metaData.book.facebookShareImageUrl || metaData.book.coverImgPath
          }
        />
      </Head>
      {!showData || isLoading ? (
        <div
          className={cn(
            'flex items-center justify-center h-screen dark:bg-dark-6',
            {
              'bg-gray-1': theme === ThemeEnum.LIGHT,
              'bg-sepia-3': theme === ThemeEnum.SEPIA,
              '!bg-dark-7': theme === ThemeEnum.DARK,
            }
          )}
        >
          <Loading size={50} />
        </div>
      ) : (
        <div
          className={cn('dark:bg-dark-6', {
            'bg-gray-1': theme === ThemeEnum.LIGHT,
            'bg-sepia-3': theme === ThemeEnum.SEPIA,
            '!bg-dark-7': theme === ThemeEnum.DARK,
          })}
        >
          <DisabledCopyContent />
          <UserReadingToLocal
            chapterId={chapterId}
            chapterPageId={data?.chapterPages?.[0]?.id}
            bookId={bookId}
            writerUserId={data?.book.penName.user.id}
            bookTitle={data?.book.title}
            chapterName={data?.chapterName}
            coverImgPath={
              data?.book.coverResizeImgPath || data?.book.coverImgPath
            }
            bookWriter={data?.book.penName.firstPenName}
            profilePageSlug={data?.book.penName.user.profilePageSlug}
          />
          {!data?.book.screenCapturable && <CaptureScreen />}

          <NavigationReadingPage
            mode={UserMissionEnum.READER}
            chapterId={chapterId}
            bookId={bookId}
            chapterName={data?.chapterName || ''}
            chapterTitle={data?.chapterTitle || ''}
            isBookOnShelf={isBookOnShelf}
            isBookmarked={isBookmarked}
          />
          <ChapterPageContainer
            className='desktop-tablet:!p-[30px_0_0] mobile:!p-[0_0_0]'
            theme={theme}
          >
            <ReadingPageHeader
              chapterName={data?.chapterName || ''}
              chapterTitle={data?.chapterTitle || ''}
              totalPage={
                data?.book?.bookType === BookEnum.MANGA &&
                data?.book?.orientation === OrientationEnum.HORIZONTAL
                  ? data?.chapterPages.length
                  : undefined
              }
            />
            {/* {isLoading && <Loading className='mx-auto' size={50} />} */}
            {data?.book?.bookType === BookEnum.NOVEL &&
              data.chapterPages !== null && (
                <>
                  <Waypoint
                    onEnter={() => handleNovelContentWayPoint(true)}
                    onLeave={() => handleNovelContentWayPoint(false)}
                  >
                    <div>
                      <TiptapViewer
                        className={cn(
                          'chapter-content text-secondary dark:text-gray-4',
                          {
                            'is-js-font': [FontFamilyEnum.JS_JINDARA].includes(
                              fontFamily
                            ),
                            'font-14': fontSize === 14,
                            'font-16': fontSize === 16,
                            'font-18': fontSize === 18,
                            'font-20': fontSize === 20,
                            'font-22': fontSize === 22,
                            'font-24': fontSize === 24,
                            'line-height-125': lineHeight === 1.25,
                            'line-height-15': lineHeight === 1.5,
                            'line-height-2': lineHeight === 2,
                            'line-height-25': lineHeight === 2.5,
                            'line-height-3': lineHeight === 3,
                            'font-notoSerif':
                              fontFamily === FontFamilyEnum.NOTO_SERIF,
                            'font-baiJamjuree':
                              fontFamily === FontFamilyEnum.BAI_JAMJUREE,
                            'font-jsJindara':
                              fontFamily === FontFamilyEnum.JS_JINDARA,
                            'font-sarabun':
                              fontFamily === FontFamilyEnum.SARABUN,
                            'font-trirong':
                              fontFamily === FontFamilyEnum.TRIRONG,
                          }
                        )}
                        value={data.chapterPages[0]?.content}
                        disableWayPoint={disableWayPoint}
                        contentDebounceTime={1500}
                        handlePercentageChange={handlePercentageChange}
                        readingPercentage={continuePercentage}
                      />
                    </div>
                  </Waypoint>
                  <CreateUserReading
                    chapterPageId={data.chapterPages[0]?.id}
                    chapterId={chapterId}
                    bookId={bookId}
                    writerUserId={data?.book.penName.user.id}
                    bookTitle={data?.book.title}
                    chapterName={data?.chapterName}
                    coverImgPath={
                      data?.book.coverResizeImgPath || data?.book.coverImgPath
                    }
                    bookWriter={data?.book.penName.firstPenName}
                    profilePageSlug={data?.book.penName.user.profilePageSlug}
                  />
                </>
              )}
            {data?.book?.bookType === BookEnum.MANGA &&
              data.book.orientation === OrientationEnum.HORIZONTAL &&
              data.chapterPages !== null && (
                <HorizontalStyle
                  chapterPages={data.chapterPages}
                  chapterId={chapterId}
                  continuePageId={continuePageId}
                />
              )}
            {data?.book?.bookType === BookEnum.MANGA &&
              data.book.orientation === OrientationEnum.VERTICAL &&
              data.chapterPages !== null && (
                <VerticalStyle
                  chapterPages={data.chapterPages}
                  continuePageId={continuePageId}
                />
              )}

            {readingPageFooterdata && (
              <ReadingPageFooter
                fontFamily={fontFamily}
                bookId={bookId}
                prevId={data?.prevId || null}
                nextId={data?.nextId || null}
                isWriter={readingPageFooterdata.isWriter}
                affiliation={readingPageFooterdata.affiliation}
                displayName={readingPageFooterdata.displayName || ''}
                penNameUserId={readingPageFooterdata.penNameUserId}
                penNameId={readingPageFooterdata.penNameId}
                penName={readingPageFooterdata.penName}
                penNameCover={readingPageFooterdata.penNameCover}
                messageFormWriter={readingPageFooterdata.messageFormWriter}
                slug={readingPageFooterdata.slug}
                bookType={readingPageFooterdata.bookType}
                isAdmin={readingPageFooterdata.isAdmin}
                email={readingPageFooterdata.email}
                isPermanentBan={readingPageFooterdata.isPermanentBan}
                mode={UserMissionEnum.READER}
              />
            )}
          </ChapterPageContainer>
          <ShareButtons
            className='mx-auto w-fit !mt-0 border-none shadow-none bg-transparent !py-[50px] mobile:!py-[40px]'
            shareUrl={shareUrl}
            bookId={bookId}
            bookChapterId={chapterId}
            type='story'
          />
          <Container className='!p-[0_0_80px]'>
            <Comment
              bookId={bookId}
              chapterId={Number(query?.chapterId)}
              authorUserId={Number(data?.book?.penName?.user?.id)}
              commentCount={Number(data?.commentCount)}
              lastCommentDate={data?.lastCommentDate}
              refecthKey={[
                'story',
                bookId,
                'chapter',
                Number(query?.chapterId),
              ]}
              commentable={data?.commentable}
              guestCommentable={data?.book?.guestCommentable}
              isUserHasBlockedComment={data?.isUserHasBlockedComment}
            />
          </Container>
          <GoToTopButton className='desktop:hidden' />
        </div>
      )}
    </div>
  )
}
